import React from 'react';
import PrimaryProfileDisconnected from 'assets/images/StatusIndicator/primaryDisconected.svg';
import SecondaryProfileDisconnected from 'assets/images/StatusIndicator/secondaryDisconnected.svg';
import InactiveBoth from 'assets/images/StatusIndicator/inactiveBoth.svg';
import PrimaryProfileActive from 'assets/images/StatusIndicator/primaryRSimConnected.svg';
import SecondaryProfileActive from 'assets/images/StatusIndicator/secondRSimConnected.svg';
import AlertrSIM from 'assets/images/StatusIndicator/AlertrSIM.svg';
import { RsimProfileStatus } from 'rSimInventory/RSimApi/rSimApi.interface';
import Box from '@mui/material/Box';
import { i18 } from 'i18n/i18n';
import { Tooltip } from 'common/components/Tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import { useKickSimContext } from './KickRSimContext';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { pxToRem } from 'src/common/utils/fonts';

type FCWithChildren = React.FC<React.PropsWithChildren>;

type StatusIndicatorProps = {
  status: RsimProfileStatus;
  euicc: string;
  hideTooltip?: boolean;
};

const statusIndicatorIconConfig = {
  width: pxToRem(24),
  height: pxToRem(24),
  role: 'img',
};

const SessionStatusBox = styled(Box)(() => ({
  display: 'flex',
  gap: 8,
  alignItems: 'center',
}));

const IconRootBox: FCWithChildren = React.forwardRef(({ children, ...props }, ref) => (
  <Box ref={ref} sx={{ position: 'relative', width: pxToRem(24), height: pxToRem(24) }} {...props}>
    {children}
  </Box>
));

const IconAlertBox: FCWithChildren = ({ children }) => (
  <Box
    sx={{
      position: 'absolute',
      top: pxToRem(-3),
      right: pxToRem(-3),
    }}
  >
    {children}
  </Box>
);

const AlertIcon = () => (
  <IconAlertBox>
    <AlertrSIM height={pxToRem(13)} data-testid="Alert icon" />
  </IconAlertBox>
);

const bothInactive = i18.t('common.unknown');

export const RSimStatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  euicc,
  hideTooltip,
}) => {
  const { isPrimaryProfileDisabled, isSecondaryProfileDisabled } = useKickSimContext();

  const alert = isPrimaryProfileDisabled(euicc) || isSecondaryProfileDisabled(euicc);

  const { t } = useTranslation();
  const alertDecriptionRows = alert ? [`${t('common.simulateOutageRunning')}!`] : [];

  if (!hideTooltip) {
    if (status === RsimProfileStatus.PrimaryProfileActive) {
      const description = [...alertDecriptionRows];
      const profileActiveLabel = t('rSimInventory.primaryProfileActive');
      return (
        <Tooltip title={profileActiveLabel} description={description}>
          <IconRootBox>
            <PrimaryProfileActive {...statusIndicatorIconConfig} aria-label={profileActiveLabel} />
            {alert && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }
    if (status === RsimProfileStatus.SecondaryProfileActive) {
      const description = [...alertDecriptionRows];
      const profileActiveLabel = t('rSimInventory.secondaryProfileActive');

      return (
        <Tooltip title={profileActiveLabel} description={description}>
          <IconRootBox>
            <SecondaryProfileActive
              {...statusIndicatorIconConfig}
              aria-label={profileActiveLabel}
            />
            {alert && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }
    if (status === RsimProfileStatus.PrimaryProfileDisconnected) {
      const description = [
        `${t('rSimInventory.lastActiveProfile')}: ${t('common.primary')}`,
        ...alertDecriptionRows,
      ];

      return (
        <Tooltip title={t('common.disconnected')} description={description}>
          <IconRootBox>
            <PrimaryProfileDisconnected
              {...statusIndicatorIconConfig}
              aria-label={`${t('common.disconnected')}: ${t(
                'rSimInventory.lastActiveProfile',
              )}: ${t('common.primary')}`}
            />
            {alert && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }
    if (status === RsimProfileStatus.SecondaryProfileDisconnected) {
      const description = [
        `${t('rSimInventory.lastActiveProfile')}: ${t('common.backup')}`,
        ...alertDecriptionRows,
      ];
      return (
        <Tooltip title={t('common.disconnected')} description={description}>
          <IconRootBox>
            <SecondaryProfileDisconnected
              {...statusIndicatorIconConfig}
              aria-label={`${t('common.disconnected')}: ${t(
                'rSimInventory.lastActiveProfile',
              )}: ${t('common.backup')}`}
            />
            {alert && <AlertIcon />}
          </IconRootBox>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={bothInactive} description={alertDecriptionRows}>
        <IconRootBox>
          <InactiveBoth {...statusIndicatorIconConfig} aria-label={bothInactive} />
          {alert && <AlertIcon />}
        </IconRootBox>
      </Tooltip>
    );
  }

  if (status === RsimProfileStatus.PrimaryProfileActive) {
    const label = t('rSimInventory.primaryProfileActive');
    return (
      <SessionStatusBox>
        <IconRootBox>
          <PrimaryProfileActive {...statusIndicatorIconConfig} aria-label={label} />
          {alert && <AlertIcon />}
        </IconRootBox>
        <Typography variant="text1" color="text.primary">
          {label}
        </Typography>
      </SessionStatusBox>
    );
  }
  if (status === RsimProfileStatus.SecondaryProfileActive) {
    const label = t('rSimInventory.secondaryProfileActive');
    return (
      <SessionStatusBox>
        <IconRootBox>
          <SecondaryProfileActive {...statusIndicatorIconConfig} aria-label={label} />
          {alert && <AlertIcon />}
        </IconRootBox>
        <Typography variant="text1" color="text.primary">
          {label}
        </Typography>
      </SessionStatusBox>
    );
  }
  if (status === RsimProfileStatus.PrimaryProfileDisconnected) {
    const label = t('common.disconnected');
    return (
      <SessionStatusBox>
        <IconRootBox>
          <PrimaryProfileDisconnected
            {...statusIndicatorIconConfig}
            aria-label={`${label}: ${t('rSimInventory.lastActiveProfile')}: ${t('common.primary')}`}
          />
          {alert && <AlertIcon />}
        </IconRootBox>
        <Typography variant="text1" color="text.primary">
          {label}
        </Typography>
      </SessionStatusBox>
    );
  }
  if (status === RsimProfileStatus.SecondaryProfileDisconnected) {
    const label = t('common.disconnected');
    return (
      <SessionStatusBox>
        <IconRootBox>
          <SecondaryProfileDisconnected
            {...statusIndicatorIconConfig}
            aria-label={`${label}: ${t('rSimInventory.lastActiveProfile')}: ${t('common.backup')}`}
          />
          {alert && <AlertIcon />}
        </IconRootBox>
        <Typography variant="text1" color="text.primary">
          {label}
        </Typography>
      </SessionStatusBox>
    );
  }

  return (
    <SessionStatusBox>
      <IconRootBox>
        <InactiveBoth {...statusIndicatorIconConfig} aria-label={bothInactive} />
        {alert && <AlertIcon />}
      </IconRootBox>
      <Typography variant="text1" color="text.primary">
        {bothInactive}
      </Typography>
    </SessionStatusBox>
  );
};
